import type { Team } from "../../../common/types.ts";
import { levelToAmount } from "../../../common/budgetLevels.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, local } from "../../util/index.ts";
import getPayroll from "./getPayroll.ts";

const keys = ["scouting", "coaching", "health", "facilities"] as const;
type Levels = Pick<Team["budget"], (typeof keys)[number]>;
type Plan = {
	season: number;
	version: number;
	payroll: number;
	revenue: number;
	recoveryStart: number;
};
type FinancialTeam = Team & { aiFinancialPlan?: Plan };
const bound = (n: number, low: number, high: number) =>
	Math.max(low, Math.min(high, n));

// Pure annual planning: all amounts are in the game's thousands of dollars.
export const planAiFinances = (input: {
	revenue: number;
	cash: number;
	payroll: number;
	reference: number;
	minPayroll: number;
	salaryCap: number;
	capType: string;
	luxuryPayroll: number;
	luxuryTax: number;
	budgetEnabled: boolean;
	rebuilding: boolean;
	levels: Levels;
	recoveryYears: number;
}) => {
	const x = input;
	const reference = Math.max(1, x.reference);
	const revenue = Math.max(0, x.revenue);
	// Positive cash can support additional roster investment, but only up to 30% of
	// the league's reference payroll each year. More cash does not raise this cap.
	const reserve = Math.min(Math.max(0, x.cash) * 0.1, reference * 0.3);
	// Old debt stays on the books. Its planning burden ramps in over three years
	// and is bounded, so a century-old negative balance cannot freeze the club.
	const recovery =
		Math.min(Math.max(0, -x.cash) * 0.02, revenue * 0.08) *
		Math.min(1, (x.recoveryYears + 1) / 3);
	const resources = (revenue + reserve - recovery) / reference;
	const base = bound(
		34 + (resources - 0.65) * 45 + (x.cash > reference ? 8 : 0),
		30,
		78,
	);
	const levels = { ...x.levels };
	for (const key of keys) {
		const priority = x.rebuilding
			? key === "coaching"
				? 12
				: key === "scouting"
					? 8
					: 0
			: key === "health"
				? 10
				: key === "coaching"
					? 6
					: 2;
		const target = bound(Math.round(base + priority), 30, 90);
		levels[key] = Math.round(
			bound(
				target,
				Math.max(1, x.levels[key] - 12),
				Math.min(100, x.levels[key] + 20),
			),
		);
	}
	const operations = x.budgetEnabled
		? keys.reduce(
				(sum, key) => sum + levelToAmount(levels[key], x.salaryCap),
				0,
			)
		: 0;
	let affordable = Math.max(
		0,
		revenue * 0.92 + reserve - recovery - operations,
	);
	if (x.capType !== "hard" && affordable > x.luxuryPayroll) {
		affordable =
			x.luxuryPayroll +
			(affordable - x.luxuryPayroll) / (1 + Math.max(0, x.luxuryTax));
	}
	// Gradual adjustment: no forced releases of existing contracts. Retain room
	// to operate while old salaries expire; new commitments still face a budget.
	const floor = Math.max(x.minPayroll, x.payroll * 0.9);
	const target = Math.max(floor, Math.min(reference * 1.2, affordable));
	const payroll = x.capType === "none" ? target : Math.min(x.salaryCap, target);
	return { levels, payroll: Math.round(payroll) };
};

export const updateAiFinances = async () => {
	if (!isSport("baseball") || g.get("forceHistoricalRosters")) return;
	const season = g.get("season");
	for (const raw of await idb.cache.teams.getAll()) {
		const t = raw as FinancialTeam;
		if (
			t.disabled ||
			(g.get("userTids").includes(t.tid) &&
				!local.autoPlayUntil &&
				!g.get("spectator"))
		)
			continue;
		if (t.aiFinancialPlan?.season === season && t.aiFinancialPlan.version === 2)
			continue;
		const rows = await idb.getCopies.teamSeasons(
			{ tid: t.tid, seasons: [season - 3, season] },
			"noCopyCache",
		);
		const current = rows.find((row) => row.season === season);
		const completed = rows
			.filter((row) => row.season < season && helpers.getTeamSeasonGp(row) > 0)
			.slice(-3);
		const payroll = await getPayroll(t.tid);
		const totalRevenue = (row: (typeof rows)[number]) =>
			Object.values(row.revenues).reduce((sum, v) => sum + v, 0);
		let revenue = completed.length
			? completed.reduce((sum, row) => sum + totalRevenue(row), 0) /
				completed.length
			: 0;
		if (revenue <= 0) {
			const gp = current ? helpers.getTeamSeasonGp(current) : 0;
			revenue =
				current && gp >= 20
					? (totalRevenue(current) * g.get("numGames")) / gp
					: Math.max(payroll, g.get("minPayroll")) / 0.7;
		}
		const recoveryStart = t.aiFinancialPlan?.recoveryStart ?? season;
		const result = planAiFinances({
			revenue,
			cash: current?.cash ?? rows.at(-1)?.cash ?? 0,
			payroll,
			reference: Math.max(
				g.get("salaryCap"),
				g.get("minPayroll"),
				g.get("luxuryPayroll"),
			),
			minPayroll: g.get("minPayroll"),
			salaryCap: g.get("salaryCap"),
			capType: g.get("salaryCapType"),
			luxuryPayroll: g.get("luxuryPayroll"),
			luxuryTax: g.get("luxuryTax"),
			budgetEnabled: g.get("budget"),
			rebuilding: t.strategy === "rebuilding",
			levels: t.budget,
			recoveryYears: Math.max(0, season - recoveryStart),
		});
		if (g.get("budget")) Object.assign(t.budget, result.levels);
		t.aiFinancialPlan = {
			season,
			version: 2,
			payroll: result.payroll,
			revenue,
			recoveryStart,
		};
		await idb.cache.teams.put(t);
	}
};

export const teamBaseballBudget = async (tid: number) => {
	const t = (await idb.cache.teams.get(tid)) as FinancialTeam | undefined;
	const plan = t?.aiFinancialPlan;
	if (
		plan?.season === g.get("season") &&
		plan.version === 2 &&
		!g.get("userTids").includes(tid)
	) {
		return g.get("salaryCapType") === "none"
			? plan.payroll
			: Math.min(plan.payroll, g.get("salaryCap"));
	}
	return g.get("salaryCapType") === "none"
		? Math.max(g.get("salaryCap"), g.get("minPayroll"), g.get("luxuryPayroll"))
		: g.get("salaryCap");
};
